import React from "react";

import { ChevronDown, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { CADENCE_ICON, CADENCE_SOLID_COLOR, CadenceKey } from "./constants";
import { GuidanceNavLink } from "../../Utils/Guidance/guidanceLinkResolver";

interface AgendaItem {
  n: number;
  item: string;
  time: string;
}

export interface CadencePlaybook {
  heading: string;
  time: string;
  nav: string;
  what: string[];
  lookfor?: string[];
  agenda?: AgendaItem[];
  links?: string[];
}

interface CadenceCardProps {
  cadenceKey: CadenceKey;
  displayLabel: string;
  playbook: CadencePlaybook;
  resolvedLinks: GuidanceNavLink[];
  isOpen: boolean;
  isToday: boolean;
  onToggle: () => void;
  onFloat: () => void;
  withOrg: (path: string) => string;
}

const CadenceCard: React.FC<CadenceCardProps> = ({
  cadenceKey,
  displayLabel,
  playbook,
  resolvedLinks,
  isOpen,
  isToday,
  onToggle,
  onFloat,
  withOrg,
}) => {
  const navigate = useNavigate();
  const solidColor = CADENCE_SOLID_COLOR[cadenceKey];

  return (
    <div
      id={`guidance-cadence-card-${cadenceKey}`}
      className={`border rounded-xl bg-white overflow-hidden transition-shadow ${
        isOpen ? "border-gray-300 shadow-sm" : "border-gray-200"
      }`}
    >
      <div className="relative w-full flex items-center gap-3.5 p-4 pl-5">
        <div
          className="absolute left-0 top-0 bottom-0 w-1"
          style={{ backgroundColor: solidColor }}
        />
        <button
          type="button"
          onClick={onToggle}
          className="flex items-center gap-3.5 flex-1 min-w-0 text-left"
        >
          <span className="text-xl shrink-0" aria-hidden>
            {CADENCE_ICON[cadenceKey]}
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-0.5">
              {displayLabel}
            </div>
            <div className="text-sm font-bold text-gray-900 truncate leading-snug">
              {playbook.heading}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">{playbook.time}</div>
          </div>
          {isToday && (
            <span className="text-[10px] font-bold uppercase tracking-wide text-white bg-purple-600 rounded-full px-2.5 py-1 shrink-0">
              Today
            </span>
          )}
        </button>
        {isOpen && (
          <button
            type="button"
            onClick={onFloat}
            className="text-xs font-semibold text-gray-400 hover:text-purple-600 shrink-0 px-1.5"
          >
            Float ↗
          </button>
        )}
        <button
          type="button"
          onClick={onToggle}
          aria-label={isOpen ? "Collapse" : "Expand"}
          className="shrink-0"
        >
          {isOpen ? (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-400" />
          )}
        </button>
      </div>

      {isOpen && (
        <div className="px-4 pb-4 pl-5 border-t border-gray-100 pt-4">
          <p className="text-xs text-gray-400 mb-3">↗ {playbook.nav}</p>

          {playbook.agenda?.length ? (
            <div className="mb-4">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Agenda
              </h4>
              <ol className="space-y-1.5">
                {playbook.agenda.map(item => (
                  <li key={item.n} className="text-sm text-gray-700 flex gap-2">
                    <span className="text-gray-400 shrink-0">{item.n}.</span>
                    <span className="flex-1">{item.item}</span>
                    <span className="text-xs text-gray-400 shrink-0">
                      {item.time}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          ) : null}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                What to do
              </h4>
              <ul className="space-y-1.5">
                {playbook.what.map((line, idx) => (
                  <li key={idx} className="text-sm text-gray-700 flex gap-1.5">
                    <span className="text-[#9aa6b6] shrink-0">•</span>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>

            {playbook.lookfor?.length ? (
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  What to look for
                </h4>
                <ul className="space-y-1.5">
                  {playbook.lookfor.map((line, idx) => (
                    <li
                      key={idx}
                      className="text-sm text-gray-700 flex gap-1.5"
                    >
                      <span className="text-[#9aa6b6] shrink-0">•</span>
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>

          {resolvedLinks.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {resolvedLinks.map(link => (
                <button
                  key={link.key}
                  type="button"
                  onClick={() => navigate(withOrg(link.path))}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-[7px] border border-[#bfcefe] bg-[#eff4ff] text-[#2563eb] hover:bg-[#dbeafe] transition-colors"
                >
                  ↗ {link.label}
                </button>
              ))}
            </div>
          )}

          <div className="text-right text-[11px] text-gray-400 italic">
            AL = Account Level · RL = Relationship Level
          </div>
        </div>
      )}
    </div>
  );
};

export default CadenceCard;
